import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { cn } from "../lib/utils";
import UnhealthyTagsTab from "../components/alerts/UnhealthyTagsTab";
import UnmatchedTagsTab from "../components/alerts/UnmatchedTagsTab";

/**
 * Alerts — a tabbed operator page for things that need attention across the
 * fleet. Platform-only (routed behind the `dashboard.tag_health` cap in
 * App.tsx + the nav). The URL (`/alerts/:tab`) drives the active tab so each
 * tab is deep-linkable.
 *
 * Tabs: Unhealthy Tags (tags that read badly) and Unmatched Tags (tags that
 * read fine but belong to no product). More alert types (down boxes,
 * stockouts, bandwidth) slot into `TABS` without a rewrite. `TABS[0]` is
 * always visible, so `active` never falls through.
 */
interface TabDef {
  key: string;
  label: string;
  node: React.ReactNode;
}

const TABS: TabDef[] = [
  { key: "unhealthy-tags", label: "Unhealthy Tags", node: <UnhealthyTagsTab /> },
  { key: "unmatched-tags", label: "Unmatched Tags", node: <UnmatchedTagsTab /> },
];

export const AlertsPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();

  const active = TABS.find((t) => t.key === tab);
  if (!active) {
    return <Navigate to={`/alerts/${TABS[0].key}`} replace />;
  }

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-bold mb-4">Alerts</h1>

      <nav className="flex flex-wrap gap-1 border-b border-secondary-200 mb-4">
        {TABS.map((t) => {
          const isActive = t.key === active.key;
          return (
            <Link
              key={t.key}
              to={`/alerts/${t.key}`}
              className={cn(
                "px-3 py-2 text-sm rounded-t-md -mb-px border-b-2 transition-colors",
                isActive
                  ? "border-primary-500 text-primary-700 font-semibold bg-white"
                  : "border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50",
              )}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>

      <section>{active.node}</section>
    </div>
  );
};

export default AlertsPage;
